import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTheme } from '@mui/material/styles';
import MedicalHistoryDisplay from './MedicalHistoryDisplay';
import ImportSurgicalHistoryDialog from './ImportSurgicalHistoryDialog';
import RichTextEditor from 'components/RichTextEditor';
import MDDatePicker from 'components/MDDatePicker';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import { api } from '../../../api';
import Swal from 'sweetalert2';

const GENERIC_FIELD_SLUGS = {
  patient_history: 'history-common-params-previous-history',
  family_history: 'history-common-params-family-history',
  allergic_to_drugs: 'history-common-params-allergic-to-drug',
  drug_medical_history: 'history-common-params-drug-medical-history',
  previous_infertility_treatment: 'history-common-params-prev-infertility-treatment',
  notes: 'history-common-params-note',
};

const GENERIC_FIELD_LABELS = [
  { key: 'patient_history', label: 'Personal History' },
  { key: 'family_history', label: 'Family History' },
  { key: 'allergic_to_drugs', label: 'Allergic to Drugs' },
  { key: 'drug_medical_history', label: 'Drug / Medical History' },
  { key: 'previous_infertility_treatment', label: 'Previous Infertility Treatment' },
  { key: 'notes', label: 'Notes' },
];

const defaultFormValues = {
  patient_history: '',
  family_history: '',
  allergic_to_drugs: '',
  drug_medical_history: '',
  previous_infertility_treatment: '',
  notes: '',
};

const emptyRow = () => ({
  surgery: '',
  history_date: '',
  notes: '',
});

const DOC_TYPES = [
  { label: 'Aadhaar', key: 'aadhaar', showIssuedBy: false, showValidTill: false },
  { label: 'PAN', key: 'pan', showIssuedBy: false, showValidTill: false },
  { label: 'Voter Card', key: 'voter', showIssuedBy: false, showValidTill: false },
  { label: 'Passport', key: 'passport', showIssuedBy: true, showValidTill: true },
  { label: 'Driving License', key: 'driving-liciense', showIssuedBy: true, showValidTill: true },
];

const emptyAttachment = {
  docType: '',
  number: '',
  issued_by: '',
  valid_till: '',
  front: null,
  back: null,
};

function DonorMedicalHistorySection({ contactId, donorInfo, genericHistory, surgicalHistory, procedureId, procedureHistories, canSave, canEdit, canDelete, onSaved }) {
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';

  const [showForm, setShowForm] = useState(false);
  const [showSurgicalForm, setShowSurgicalForm] = useState(false);
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [surgicalRows, setSurgicalRows] = useState([emptyRow()]);
  const [historyParams, setHistoryParams] = useState(null);
  const [surgicalOptions, setSurgicalOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [paramsLoading, setParamsLoading] = useState(false);
  const [editingSurgicalRecord, setEditingSurgicalRecord] = useState(null);
  const [donorDocuments, setDonorDocuments] = useState([]);
  const [donorDocumentsLoading, setDonorDocumentsLoading] = useState(false);
  const [attachmentForm, setAttachmentForm] = useState(emptyAttachment);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [donorVisits, setDonorVisits] = useState([]);
  const [selectedSurgicalVisit, setSelectedSurgicalVisit] = useState('ALL_VISITS');
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [importingSurgicalHistory, setImportingSurgicalHistory] = useState(false);
  const [showSurgicalImportDialog, setShowSurgicalImportDialog] = useState(false);
  const [procedureSurgicalHistories, setProcedureSurgicalHistories] = useState([]);

  // Extract procedure surgical histories filtered by contact ID
  useEffect(() => {
    if (!procedureHistories?.length || !contactId) {
      setProcedureSurgicalHistories([]);
      return;
    }
    const surgicalRecords = procedureHistories.filter(
      h => h.param_group_member?.slug === 'primary-history-common-params-surgical-history' &&
           h.irm_contacts_id === contactId
    );
    setProcedureSurgicalHistories(surgicalRecords);
  }, [procedureHistories, contactId]);

  // Load params on component mount
  useEffect(() => {
    if (!historyParams && !paramsLoading) {
      loadParams();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load params when either form is first opened
  useEffect(() => {
    if ((showForm || showSurgicalForm) && !historyParams) {
      loadParams();
    }
  }, [showForm, showSurgicalForm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill form with existing history when opening form
  useEffect(() => {
    if (showForm && genericHistory) {
      setFormValues({
        patient_history: genericHistory.patient_history || '',
        family_history: genericHistory.family_history || '',
        allergic_to_drugs: genericHistory.allergic_to_drugs || '',
        drug_medical_history: genericHistory.drug_medical_history || '',
        previous_infertility_treatment: genericHistory.previous_infertility_treatment || '',
        notes: genericHistory.notes || '',
      });
    } else if (showForm) {
      setFormValues(defaultFormValues);
    }
  }, [showForm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadDonorDocuments = async () => {
      if (!donorInfo?.external_code) {
        setDonorDocuments([]);
        return;
      }
      setDonorDocumentsLoading(true);
      try {
        const res = await api.contacts.getDonorDetails(donorInfo.external_code);
        const donor = res?.data?.donor || res?.data || null;
        setDonorDocuments(donor?.documents || []);
      } catch (err) {
        console.error('Error loading donor documents:', err);
        setDonorDocuments([]);
      } finally {
        setDonorDocumentsLoading(false);
      }
    };

    loadDonorDocuments();
  }, [donorInfo?.external_code]);

  useEffect(() => {
    const loadDonorVisits = async () => {
      if (!showSurgicalForm || !contactId) return;
      setVisitsLoading(true);
      try {
        const res = await api.contacts.getAllVisitsByContact(contactId);
        const sortedVisits = Array.isArray(res?.data)
          ? [...res.data].sort((a, b) => b.visit_no - a.visit_no)
          : [];
        setDonorVisits(sortedVisits);
      } catch (err) {
        console.error('Error loading donor visits for surgical import:', err);
        setDonorVisits([]);
      } finally {
        setVisitsLoading(false);
      }
    };

    loadDonorVisits();
  }, [showSurgicalForm, contactId]);

  const loadParams = useCallback(async () => {
    setParamsLoading(true);
    try {
      const response = await api.opd.getHistoryParams();
      const histParams = response?.data?.history_params || null;
      setHistoryParams(histParams);

      // Extract surgical options from primary_history_params
      const primaryParams = response?.data?.primary_history_params;
      if (primaryParams?.param_group_members && Array.isArray(primaryParams.param_group_members)) {
        const surgicalMember = primaryParams.param_group_members.find((m) => m && m.name === 'Surgical History');
        if (surgicalMember?.param_group_member_options && Array.isArray(surgicalMember.param_group_member_options)) {
          const filteredOptions = surgicalMember.param_group_member_options
            .filter((opt) => {
              return opt !== null && 
                     opt !== undefined && 
                     typeof opt === 'object' &&
                     opt.id && 
                     opt.option_value;
            })
            .map((opt) => ({
              value: opt.option_value,
              label: opt.option_value,
              param_group_member_id: surgicalMember.id,
              param_group_member_option_id: opt.id,
              param_group_id: surgicalMember.irm_param_groups_id,
            }))
            .filter(opt => opt.param_group_member_option_id && opt.value); // Double-check after mapping
          console.log('DonorMedicalHistorySection - Loaded surgical options:', filteredOptions);
          console.log('DonorMedicalHistorySection - Has any null?', filteredOptions.some(o => o === null || o === undefined));
          setSurgicalOptions(filteredOptions);
        } else {
          setSurgicalOptions([]);
        }
      } else {
        setSurgicalOptions([]);
      }
    } catch (err) {
      console.error('Error loading history params for donor medical history:', err);
      setSurgicalOptions([]); // Set empty array on error
    } finally {
      setParamsLoading(false);
    }
  }, []);

  const handleGenericFieldChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSurgicalRowChange = (idx, field, value) => {
    setSurgicalRows((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  const handleAddSurgicalRow = () => {
    setSurgicalRows((prev) => [...prev, emptyRow()]);
  };

  const handleRemoveSurgicalRow = (idx) => {
    setSurgicalRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleEditSurgical = (item) => {
    setEditingSurgicalRecord({
      ...item,
      surgery: item.surgery || item.value || '',
      notes: item.remarks || item.notes || '',
      history_date: item.history_date || '',
      param_group_member_option_id:
        item.param_group_member_option_id || item.param_group_member_option_id || (Array.isArray(surgicalOptions) ? surgicalOptions : []).find((opt) => opt && opt.value === (item.surgery || item.value))?.param_group_member_option_id || null,
    });
  };

  const handleAddNewSurgical = () => {
    if (!Array.isArray(surgicalOptions) || !surgicalOptions.length) {
      Swal.fire({ icon: 'warning', title: 'Not Ready', text: 'Surgical options are still loading.' });
      return;
    }
    // Add a new blank record to the list in edit mode
    const newRecord = {
      id: `new_${Date.now()}`,
      isNew: true,
      value: '',
      surgery: '',
      history_date: '',
      remarks: '',
      notes: '',
      irm_contacts_id: contactId,
      param_group_member_option_id: null,
      param_group_member: {
        slug: 'primary-history-common-params-surgical-history',
      },
    };
    setProcedureSurgicalHistories(prev => [newRecord, ...prev]);
    setEditingSurgicalRecord({
      ...newRecord,
      isNew: true,
      surgery: '',
      notes: '',
      history_date: '',
      param_group_member_option_id: null,
    });
  };

  const handleSaveNewSurgical = async () => {
    if (!editingSurgicalRecord || !editingSurgicalRecord.isNew) return;
    
    const selected = (Array.isArray(surgicalOptions) ? surgicalOptions : []).find(
      (opt) => opt && opt.param_group_member_option_id === editingSurgicalRecord.param_group_member_option_id,
    );

    if (!selected) {
      Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please select a valid surgery.' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        irm_procedures_id: procedureId,
        irm_contacts_id: contactId,
        irm_param_groups_id: selected.param_group_id,
        irm_param_group_members_id: selected.param_group_member_id,
        irm_param_group_member_options_id: selected.param_group_member_option_id || null,
        value: selected.value || selected.label,
        history_date: editingSurgicalRecord.history_date || null,
        remarks: editingSurgicalRecord.notes || null,
      };
      
      const response = await api.ivf.createProcedureHistory(payload);
      const newId = response?.data?.id || response?.data?.irm_contact_histories_id;

      // Update local state with saved record
      setProcedureSurgicalHistories(prev =>
        prev.map(item => {
          if (item.id === editingSurgicalRecord.id) {
            return {
              ...item,
              id: newId,
              isNew: false,
              value: selected.value || selected.label,
              history_date: editingSurgicalRecord.history_date,
              remarks: editingSurgicalRecord.notes,
              irm_param_group_member_options_id: selected.param_group_member_option_id,
              param_group_member_option: {
                value: selected.value,
                option_value: selected.label,
              },
            };
          }
          return item;
        })
      );

      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: 'New surgical record added successfully.',
        timer: 1500,
        showConfirmButton: false,
      });
      setEditingSurgicalRecord(null);
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving new surgical history:', err);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err?.response?.data?.message || 'Failed to save new surgical history.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (editingSurgicalRecord?.isNew) {
      // Remove the unsaved new record
      setProcedureSurgicalHistories(prev => prev.filter(item => item.id !== editingSurgicalRecord.id));
    }
    setEditingSurgicalRecord(null);
  };

  const handleUpdateSurgical = async () => {
    if (!editingSurgicalRecord?.id) return;
    const safeOptions = Array.isArray(surgicalOptions) ? surgicalOptions : [];
    const selected = safeOptions.find(
      (opt) => opt && opt.param_group_member_option_id === editingSurgicalRecord.param_group_member_option_id,
    ) || safeOptions.find((opt) => opt && opt.value === editingSurgicalRecord.surgery);

    if (!selected) {
      Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please select a valid surgery.' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        irm_procedures_id: procedureId,
        irm_contacts_id: contactId,
        irm_param_groups_id: selected.param_group_id,
        irm_param_group_members_id: selected.param_group_member_id || editingSurgicalRecord.irm_param_group_members_id,
        irm_param_group_member_options_id: selected.param_group_member_option_id || null,
        value: selected.value || selected.label,
        history_date: editingSurgicalRecord.history_date || null,
        remarks: editingSurgicalRecord.notes || null,
      };
      
      await api.ivf.updateProcedureHistory(editingSurgicalRecord.id || editingSurgicalRecord.irm_contact_histories_id, payload);

      // Update local state
      setProcedureSurgicalHistories(prev =>
        prev.map(item => {
          if (item.id === editingSurgicalRecord.id || item.irm_contact_histories_id === editingSurgicalRecord.id) {
            return {
              ...item,
              value: selected.value || selected.label,
              history_date: editingSurgicalRecord.history_date,
              remarks: editingSurgicalRecord.notes,
              irm_param_group_member_options_id: selected.param_group_member_option_id,
              param_group_member_option: {
                value: selected.value,
                option_value: selected.label,
              },
            };
          }
          return item;
        })
      );

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Donor surgical history updated successfully.',
        timer: 1500,
        showConfirmButton: false,
      });
      setEditingSurgicalRecord(null);
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error updating donor surgical history:', err);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err?.response?.data?.message || 'Failed to update surgical history.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSurgical = async (item) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Delete?',
      text: `Remove donor surgical history: ${item.surgery || item.value}?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    try {
      // Use procedure history API to delete
      await api.ivf.deleteProcedureHistory(item.id || item.irm_contact_histories_id);
      
      // Update local state
      setProcedureSurgicalHistories(prev => prev.filter(h => h.id !== item.id && h.irm_contact_histories_id !== item.id));
      
      Swal.fire({ 
        icon: 'success', 
        title: 'Deleted', 
        text: 'Surgical record deleted successfully.',
        timer: 1200, 
        showConfirmButton: false 
      });
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error deleting donor surgical history:', err);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: err?.response?.data?.message || 'Failed to delete surgical history.',
      });
    }
  };

  const handleAttachmentFieldChange = (field, value) => {
    setAttachmentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAttachmentUpload = async () => {
    const selectedType = DOC_TYPES.find((doc) => doc.key === attachmentForm.docType);
    if (!selectedType) {
      Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please select a document type.' });
      return;
    }
    if (!attachmentForm.number.trim()) {
      Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please enter a document number.' });
      return;
    }
    if (!attachmentForm.front) {
      Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please select the front file.' });
      return;
    }

    setAttachmentUploading(true);
    try {
      const fd = new FormData();
      fd.append('contact_id', contactId);
      fd.append('document_number', attachmentForm.number.trim().toUpperCase());
      fd.append('type', selectedType.key === 'voter' ? 'epic' : selectedType.key);
      fd.append('file1', attachmentForm.front);
      fd.append('is_default', 'Y');
      fd.append('file_sub_type_1', `front_${selectedType.key}`);
      if (attachmentForm.back) {
        fd.append('file2', attachmentForm.back);
        fd.append('file_sub_type_2', `back_${selectedType.key}`);
      }
      if (selectedType.showIssuedBy && attachmentForm.issued_by.trim()) {
        fd.append('issued_by', attachmentForm.issued_by.trim().toUpperCase());
      }
      if (selectedType.showValidTill && attachmentForm.valid_till) {
        fd.append('valid_till', attachmentForm.valid_till);
      }

      await api.contacts.uploadDonorDocuments(fd);
      Swal.fire({ icon: 'success', title: 'Uploaded', text: `${selectedType.label} uploaded successfully.`, timer: 1200, showConfirmButton: false });
      setAttachmentForm(emptyAttachment);
      if (donorInfo?.external_code) {
        const res = await api.contacts.getDonorDetails(donorInfo.external_code);
        const donor = res?.data?.donor || res?.data || null;
        setDonorDocuments(donor?.documents || []);
      }
    } catch (err) {
      console.error('Error uploading donor document:', err);
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: err?.response?.data?.message || 'Failed to upload donor attachment.',
      });
    } finally {
      setAttachmentUploading(false);
    }
  };

  const selectedAttachmentType = DOC_TYPES.find((doc) => doc.key === attachmentForm.docType) || null;

  // Load existing surgical history records into the form rows for editing/reference
  const handleLoadFromExisting = () => {
    if (!surgicalHistory || surgicalHistory.length === 0) return;
    const loaded = surgicalHistory.map((item) => ({
      surgery: item.value || item.surgery || '',
      history_date: item.history_date || '',
      notes: item.remarks || item.notes || '',
    }));
    setSurgicalRows(loaded.length > 0 ? loaded : [emptyRow()]);
  };

  const handleImportFromVisit = useCallback(() => {
    if (!contactId) return;
    setShowSurgicalImportDialog(true);
  }, [contactId]);

  const handleDonorSurgicalImport = useCallback(async (selectedRows, selectedVisit) => {
    if (!Array.isArray(selectedRows) || selectedRows.length === 0) return;

    if (procedureId) {
      // Save to procedure database if procedureId is available
      setImportingSurgicalHistory(true);
      try {
        // Prepare payload array for batch save
        const procedurePayload = selectedRows.map(row => ({
          irm_procedures_id: procedureId,
          irm_contacts_id: contactId,
          irm_param_groups_id: row.param_group_id,
          irm_param_group_members_id: row.param_group_member_id,
          irm_param_group_member_options_id: row.param_group_member_option_id,
          irm_contact_histories_id: row.id,
          value: row.value || row.surgery,
          remarks: row.notes || '',
          history_date: row.history_date || null,
        }));

        // Save all histories in one request
        await api.ivf.saveProcedureHistories(procedureId, procedurePayload);

        // Trigger refresh callback
        if (onSaved) onSaved();

        Swal.fire({
          icon: 'success',
          title: 'Imported & Saved',
          text: `${selectedRows.length} donor surgical record${selectedRows.length > 1 ? 's' : ''} saved to procedure.`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error saving donor surgical histories:', error);
        Swal.fire({
          icon: 'error',
          title: 'Save Failed',
          text: error.message || 'Failed to save surgical histories.',
        });
      } finally {
        setImportingSurgicalHistory(false);
      }
    } else {
      // Fallback: just update local state if no procedureId
      setSelectedSurgicalVisit(selectedVisit || 'ALL_VISITS');
      setSurgicalRows(selectedRows.length > 0 ? selectedRows : [emptyRow()]);

      Swal.fire({
        icon: 'success',
        title: 'Imported',
        text: `${selectedRows.length} donor surgical record${selectedRows.length > 1 ? 's' : ''} imported.`,
        timer: 1600,
        showConfirmButton: false,
      });
    }
  }, [procedureId, contactId, onSaved]);

  const handleSave = async () => {
    if (!contactId) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Contact ID is missing.' });
      return;
    }
    if (!historyParams) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'History parameters not loaded. Please try again.' });
      return;
    }

    setSaving(true);
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const historyArray = [];

      // Generic history fields
      for (const { key } of GENERIC_FIELD_LABELS) {
        const slug = GENERIC_FIELD_SLUGS[key];
        const value = formValues[key];
        if (value && value.trim() && value.trim() !== '<p></p>' && value.trim() !== '<p><br></p>') {
          const paramMember = historyParams?.param_group_members?.find(
            (m) => m.slug === slug && m.is_active === 'Y',
          );
          if (paramMember) {
            historyArray.push({
              contact_id: contactId,
              param_group_id: paramMember.irm_param_groups_id,
              param_group_member_id: paramMember.id,
              param_group_member_option_id: null,
              name: paramMember.name,
              value,
              history_date: currentDate,
              remarks: null,
              private_note: null,
            });
          } else {
            console.warn(`Param member not found for slug: ${slug}`);
          }
        }
      }

      console.log('Generic history to save:', historyArray);

      if (historyArray.length === 0) {
        Swal.fire({ icon: 'info', title: 'No Data', text: 'Please fill in at least one field before saving.' });
        return;
      }

      console.log('Sending to API:', historyArray);
      await api.opd.createContactHistory(historyArray);
      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: 'Donor medical history saved successfully.',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      setShowForm(false);
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving donor medical history:', err);
      console.error('Error response:', err?.response?.data);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err?.response?.data?.message || 'Failed to save medical history.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSurgical = async () => {
    if (!contactId) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Contact ID is missing.' });
      return;
    }
    if (surgicalOptions.length === 0) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Surgical options not loaded. Please try again.' });
      return;
    }

    setSaving(true);
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const historyArray = [];

      // Surgical history rows
      for (const row of surgicalRows) {
        if (!row.surgery) continue;
        const opt = (Array.isArray(surgicalOptions) ? surgicalOptions : []).find((o) => o && o.value === row.surgery);
        if (!opt) {
          console.warn(`Surgical option not found for: ${row.surgery}`);
          continue;
        }
        historyArray.push({
          contact_id: contactId,
          param_group_id: opt.param_group_id || 3,
          param_group_member_id: opt.param_group_member_id,
          param_group_member_option_id: opt.param_group_member_option_id || null,
          name: 'Surgical History',
          value: row.surgery,
          history_date: row.history_date || null,
          remarks: row.notes || null,
          private_note: null,
        });
      }

      if (historyArray.length === 0) {
        Swal.fire({ icon: 'info', title: 'No Data', text: 'Please select at least one surgery before saving.' });
        return;
      }

      console.log('Sending surgical history to API:', historyArray);
      await api.opd.createContactHistory(historyArray);
      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: 'Donor surgical history saved successfully.',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      setSurgicalRows([emptyRow()]);
      setShowSurgicalForm(false);
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving donor surgical history:', err);
      console.error('Error response:', err?.response?.data);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err?.response?.data?.message || 'Failed to save surgical history.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleForm = () => {
    setShowForm((prev) => !prev);
  };

  return (
    <Box>
      {/* ── MEDICAL HISTORY SECTION ─────────────────────────────── */}
      <Box>
        {/* Display existing generic history */}
        <MedicalHistoryDisplay genericHistory={genericHistory} />

        {canSave && (
          <Box mt={1} display="flex" justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon sx={{ fontSize: '14px !important' }} />}
              onClick={handleToggleForm}
              sx={{
                borderColor: primaryColor,
                color: primaryColor,
                textTransform: 'none',
                fontSize: '0.72rem',
                py: 0.4,
                px: 1.2,
                '&:hover': { backgroundColor: `${primaryColor}10` },
              }}
            >
              {showForm ? 'Hide Medical History Form' : 'Add / Edit Medical History'}
            </Button>
          </Box>
        )}

        {showForm && (
          <Box mt={1.5} p={2} sx={{ border: `1px solid ${primaryColor}30`, borderRadius: 1, backgroundColor: '#fafcfc' }}>
            {paramsLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} sx={{ color: primaryColor }} />
              </Box>
            ) : (
              <>
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: primaryColor,
                    fontSize: '11px',
                    mb: 1.5,
                    borderBottom: `2px solid ${primaryColor}`,
                    pb: '4px',
                  }}
                >
                  Medical History Fields
                </Typography>
                <Grid container spacing={1.5}>
                  {GENERIC_FIELD_LABELS.map(({ key, label }) => (
                    <Grid item xs={12} key={key}>
                      <RichTextEditor
                        label={label}
                        value={formValues[key]}
                        onChange={(val) => handleGenericFieldChange(key, val)}
                        height="90px"
                        placeholder={`Enter ${label.toLowerCase()}...`}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={
                      saving ? (
                        <CircularProgress size={14} sx={{ color: 'white' }} />
                      ) : (
                        <SaveIcon sx={{ fontSize: '14px !important' }} />
                      )
                    }
                    onClick={handleSave}
                    disabled={saving || !historyParams}
                    sx={{
                      backgroundColor: primaryColor,
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '0.72rem',
                      '&:hover': { backgroundColor: '#005f70' },
                      '&:disabled': { backgroundColor: '#ccc' },
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Medical History'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* ── SURGICAL HISTORY SECTION (separate) ─────────────────── */}
      <Divider sx={{ my: 2 }} />
      <Paper
        elevation={0}
        sx={{
          border: `1.5px solid ${primaryColor}30`,
          borderRadius: 1.5,
          backgroundColor: `${primaryColor}08`,
          p: 1.5,
        }}
      >
        {/* Section header with buttons */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
          <Typography
            sx={{
              fontWeight: 'bold',
              color: primaryColor,
              fontSize: '12px',
              borderBottom: `2px solid ${primaryColor}`,
              pb: '3px',
              flexShrink: 0,
            }}
          >
            Surgical History
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {canSave && (
              <Button
                size="small"
                variant="outlined"
                startIcon={importingSurgicalHistory ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <CloudDownloadIcon sx={{ fontSize: '14px !important' }} />}
                onClick={handleImportFromVisit}
                disabled={visitsLoading || importingSurgicalHistory}
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  py: 0.6,
                  px: 1.2,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: `${primaryColor}10` },
                }}
              >
                {importingSurgicalHistory ? 'Loading...' : 'Import from Old Visit'}
              </Button>
            )}
            {canSave && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                onClick={handleAddNewSurgical}
                disabled={!!editingSurgicalRecord}
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  py: 0.6,
                  px: 1.2,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: `${primaryColor}10` },
                }}
              >
                Add New
              </Button>
            )}
          </Box>
        </Box>

        {/* Existing surgical records table */}
        {procedureSurgicalHistories.length > 0 && (
          <TableContainer component={Paper} sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '12px', width: '35%', py: 1 }}>Surgery</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '12px', width: '15%', py: 1 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '12px', width: '35%', py: 1 }}>Remarks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '12px', width: '15%', py: 1, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
                {procedureSurgicalHistories.map((item, idx) => {
                  const isEditing = editingSurgicalRecord?.id === item.id;

                  return (
                    <TableRow key={item.id || idx} hover sx={{ backgroundColor: isEditing ? `${primaryColor}08` : 'inherit' }}>
                      <TableCell sx={{ fontSize: '12px', py: 1, fontWeight: isEditing ? 600 : 400, color: isEditing ? primaryColor : 'inherit' }}>
                        {isEditing ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={editingSurgicalRecord.param_group_member_option_id || ''}
                              onChange={(e) => {
                                const selected = (Array.isArray(surgicalOptions) ? surgicalOptions : []).find((opt) => opt && opt.param_group_member_option_id === e.target.value);
                                setEditingSurgicalRecord((prev) => ({
                                  ...prev,
                                  surgery: selected?.label || '',
                                  param_group_member_option_id: selected?.param_group_member_option_id || null,
                                  param_group_member_id: selected?.param_group_member_id || prev.param_group_member_id,
                                }));
                              }}
                              sx={{ fontSize: '0.875rem', height: 40 }}
                            >
                              {(() => {
                                const safeOptions = Array.isArray(surgicalOptions) ? surgicalOptions : [];
                                return safeOptions
                                  .filter(opt => {
                                    if (!opt || typeof opt !== 'object') return false;
                                    if (!opt.param_group_member_option_id) return false;
                                    if (!opt.label) return false;
                                    return true;
                                  })
                                  .map((opt) => (
                                    <MenuItem key={opt.param_group_member_option_id} value={opt.param_group_member_option_id} sx={{ fontSize: '0.875rem', textTransform: 'uppercase' }}>
                                      {opt.label}
                                    </MenuItem>
                                  ));
                              })()}
                            </Select>
                          </FormControl>
                        ) : (
                          <>
                            <Box sx={{ textTransform: 'uppercase' }}>{item.value || item.surgery || '-'}</Box>
                            {item.visit_no && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                Visit #{String(item.visit_no).padStart(4, '0')}
                              </Typography>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <Box sx={{ minWidth: 160 }}>
                            <MDDatePicker
                              label="Date"
                              value={editingSurgicalRecord.history_date || ''}
                              onChange={(date) => setEditingSurgicalRecord((prev) => ({ ...prev, history_date: date || '' }))}
                              textFieldProps={{
                                fullWidth: true,
                                size: 'small',
                                sx: {
                                  '& .MuiOutlinedInput-root': { 
                                    height: 45, 
                                    fontSize: '0.9rem',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '6px',
                                  },
                                  '& .MuiInputLabel-root': { 
                                    fontSize: '0.9rem',
                                    color: primaryColor,
                                  },
                                  '& .MuiInputLabel-shrink': {
                                    backgroundColor: 'white',
                                    px: 0.5,
                                  },
                                },
                              }}
                            />
                          </Box>
                        ) : item.history_date ? formatDateForDisplay(item.history_date) : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px', py: 1, maxWidth: 160 }}>
                        {isEditing ? (
                          <RichTextEditor
                            value={editingSurgicalRecord.notes || ''}
                            onChange={(val) => setEditingSurgicalRecord((prev) => ({ ...prev, notes: val || '' }))}
                            height="80px"
                            placeholder="Optional remarks..."
                          />
                        ) : (
                          <Box dangerouslySetInnerHTML={{ __html: item.remarks || item.notes || '-' }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1, textAlign: 'center' }}>
                        {isEditing ? (
                          <Box display="flex" gap={0.5} justifyContent="center">
                            <Tooltip title="Save">
                              <IconButton
                                size="small"
                                onClick={item.isNew ? handleSaveNewSurgical : handleUpdateSurgical}
                                disabled={saving}
                                sx={{ color: '#2e7d32' }}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                disabled={saving}
                                sx={{ color: '#6c757d' }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box display="flex" gap={0.5} justifyContent="center">
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSurgical(item)}
                                  disabled={!!editingSurgicalRecord}
                                  sx={{ color: primaryColor }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSurgical(item)}
                                  disabled={!!editingSurgicalRecord}
                                  sx={{ color: primaryColor }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Show message when no records */}
        {procedureSurgicalHistories.length === 0 && !visitsLoading && donorVisits.length > 0 && (
          <Box textAlign="center" py={3} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px dashed #dee2e6' }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
              No surgical history. Click &ldquo;Import from Old Visit&rdquo; to load from previous visits, or &ldquo;Add New&rdquo; to create a new record.
            </Typography>
          </Box>
        )}
        {procedureSurgicalHistories.length === 0 && donorVisits.length === 0 && (
          <Box textAlign="center" py={3} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px dashed #dee2e6' }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
              No surgical history found. {canSave && 'Click "Add New" to create a record.'}
            </Typography>
          </Box>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography
              sx={{
                fontWeight: 'bold',
                color: primaryColor,
                fontSize: '12px',
                borderBottom: `2px solid ${primaryColor}`,
                pb: '3px',
              }}
            >
              Donor Attachments
            </Typography>
            {donorInfo?.external_code && (
              <Typography variant="caption" color="text.secondary">
                {donorInfo.external_code}
              </Typography>
            )}
          </Box>

          {donorDocumentsLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={22} sx={{ color: primaryColor }} />
            </Box>
          ) : donorDocuments.length > 0 ? (
            <TableContainer component={Box} sx={{ mb: 1.5 }}>
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.72rem', py: 0.5 }}>Document Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.72rem', py: 0.5 }}>Number</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.72rem', py: 0.5 }}>Valid Till</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.72rem', py: 0.5 }}>Files</TableCell>
                  </TableRow>
                  {donorDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <DescriptionIcon sx={{ fontSize: 14, color: primaryColor }} />
                          {doc.documentType?.type_name || '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>{doc.document_number || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>
                        {doc.valid_till ? new Date(doc.valid_till).toLocaleDateString('en-GB') : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {doc.files?.length > 0 ? doc.files.map((file) => {
                            const fileUrl = `${file.base_url}${file.file_path}`;
                            const label = file.sub_type ? file.sub_type.split('_')[0] : 'View';
                            return (
                              <Tooltip title={file.sub_type?.replace(/_/g, ' ') || 'View file'} key={file.id}>
                                <IconButton size="small" onClick={() => window.open(fileUrl, '_blank')} sx={{ color: primaryColor, border: `1px solid ${primaryColor}50`, borderRadius: '6px' }}>
                                  <VisibilityIcon sx={{ fontSize: 14 }} />
                                  <Typography sx={{ ml: 0.5, fontSize: '0.68rem', fontWeight: 600, textTransform: 'capitalize' }}>{label}</Typography>
                                </IconButton>
                              </Tooltip>
                            );
                          }) : <Typography variant="caption" color="text.disabled">No files</Typography>}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', mb: 1.5 }}>
              No donor attachments uploaded yet.
            </Typography>
          )}

          {canSave && (
            <Box sx={{ border: `1px solid ${primaryColor}25`, borderRadius: 1.5, p: 1.5, backgroundColor: '#fbfdfd' }}>
              <Typography sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '11px', mb: 1.25 }}>
                Upload Attachment
              </Typography>
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Document Type</InputLabel>
                    <Select
                      value={attachmentForm.docType}
                      label="Document Type"
                      onChange={(e) => handleAttachmentFieldChange('docType', e.target.value)}
                      sx={{ fontSize: '0.75rem', height: 40 }}
                    >
                      {DOC_TYPES.map((doc) => (
                        <MenuItem key={doc.key} value={doc.key} sx={{ fontSize: '0.75rem' }}>
                          {doc.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Document Number"
                    value={attachmentForm.number}
                    onChange={(e) => handleAttachmentFieldChange('number', e.target.value.toUpperCase())}
                    sx={{ '& .MuiOutlinedInput-root': { height: 40, fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
                  />
                </Grid>
                {selectedAttachmentType?.showIssuedBy && (
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Issued By"
                      value={attachmentForm.issued_by}
                      onChange={(e) => handleAttachmentFieldChange('issued_by', e.target.value.toUpperCase())}
                      sx={{ '& .MuiOutlinedInput-root': { height: 40, fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                )}
                {selectedAttachmentType?.showValidTill && (
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Valid Till"
                      value={attachmentForm.valid_till}
                      onChange={(e) => handleAttachmentFieldChange('valid_till', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { height: 40, fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={3}>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ height: 40, textTransform: 'none', fontSize: '0.75rem', borderColor: primaryColor, color: primaryColor }}
                  >
                    {attachmentForm.front ? attachmentForm.front.name : 'Front File *'}
                    <input hidden type="file" onChange={(e) => handleAttachmentFieldChange('front', e.target.files?.[0] || null)} />
                  </Button>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ height: 40, textTransform: 'none', fontSize: '0.75rem', borderColor: primaryColor, color: primaryColor }}
                  >
                    {attachmentForm.back ? attachmentForm.back.name : 'Back File'}
                    <input hidden type="file" onChange={(e) => handleAttachmentFieldChange('back', e.target.files?.[0] || null)} />
                  </Button>
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" mt={1.5}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={attachmentUploading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <CloudUploadIcon sx={{ fontSize: '14px !important' }} />}
                  onClick={handleAttachmentUpload}
                  disabled={attachmentUploading || !contactId}
                  sx={{
                    backgroundColor: primaryColor,
                    color: 'white',
                    textTransform: 'none',
                    fontSize: '0.72rem',
                    '&:hover': { backgroundColor: '#005f70' },
                    '&:disabled': { backgroundColor: '#ccc' },
                  }}
                >
                  {attachmentUploading ? 'Uploading...' : 'Upload Attachment'}
                </Button>
              </Box>
            </Box>
          )}
      </Box>

      {/* Import Dialog */}
      <ImportSurgicalHistoryDialog
        open={showSurgicalImportDialog}
        onClose={() => setShowSurgicalImportDialog(false)}
        contactId={contactId}
        visitOptions={donorVisits}
        sectionLabel="Donor"
        onImport={handleDonorSurgicalImport}
        primaryColor={primaryColor}
      />
    </Box>
  );
}

DonorMedicalHistorySection.propTypes = {
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  donorInfo: PropTypes.object,
  genericHistory: PropTypes.object,
  surgicalHistory: PropTypes.array,
  procedureHistories: PropTypes.array,
  procedureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canSave: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onSaved: PropTypes.func,
};

DonorMedicalHistorySection.defaultProps = {
  canSave: false,
  canEdit: false,
  canDelete: false,
  donorInfo: null,
  surgicalHistory: [],
  procedureHistories: [],
};

export default DonorMedicalHistorySection;
